/**
 * Letture del motore e-commerce da Supabase con RLS (16 sez. 3.1): commerce_stores, product_sync_jobs,
 * commerce_events, commerce_change_log, commerce_snapshots si leggono con settings_ecommerce.view;
 * get_commerce_store_labels() basta inventory.view (15 D2). Nessuna scrittura: le azioni sono in api.ts.
 * Prima della migration di fase 1 le tabelle non esistono: ogni lettura restituisce un errore gestito
 * (mai eccezioni), e presenza/menu considerano "nessun negozio".
 */
import { supabase } from '../supabase';
import { currentOrgId, NO_ORG } from './orgScope';
import {
  CHANGE_COLUMNS, EVENT_COLUMNS, JOB_COLUMNS, SNAPSHOT_COLUMNS, STORE_COLUMNS,
  type ChangeLogRow, type CommerceEventRow, type CommerceJobRow, type CommerceStoreRow, type SnapshotRow, type StoreLabel,
} from './types';

export type QueryResult<T> = { data: T; error: null } | { data: null; error: string };

const fail = <T>(e: { message?: string } | null | undefined): QueryResult<T> => ({ data: null, error: e?.message || 'errore di lettura' });
const noOrg = <T>(): QueryResult<T> => ({ data: null, error: NO_ORG });

export async function fetchStores(includeDisconnected = true): Promise<QueryResult<CommerceStoreRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('commerce_stores').select(STORE_COLUMNS).eq('organization_id', org).order('created_at', { ascending: true });
  if (!includeDisconnected) q = q.neq('status', 'disconnected');
  const { data, error } = await q;
  return error ? fail(error) : { data: (data ?? []) as unknown as CommerceStoreRow[], error: null };
}

export async function fetchStore(storeId: string): Promise<QueryResult<CommerceStoreRow | null>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  const { data, error } = await supabase.from('commerce_stores').select(STORE_COLUMNS).eq('organization_id', org).eq('id', storeId).maybeSingle();
  return error ? fail(error) : { data: (data ?? null) as unknown as CommerceStoreRow | null, error: null };
}

export async function fetchStoreLabels(): Promise<QueryResult<StoreLabel[]>> {
  const { data, error } = await supabase.rpc('get_commerce_store_labels');
  return error ? fail(error) : { data: (data ?? []) as StoreLabel[], error: null };
}

/** WooCommerce sul sistema vecchio: riga attiva in api_configurations (15 sez. 3, hasLegacyWoo), a meno che un negozio sul motore lo abbia gia' sostituito (settings.legacy = 'writer'). */
export async function fetchLegacyWooActive(organizationId: string): Promise<boolean> {
  const { data: switched } = await supabase
    .from('commerce_stores')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('provider', 'woocommerce')
    .neq('status', 'disconnected')
    .eq('settings->>legacy', 'writer')
    .limit(1);
  if ((switched?.length ?? 0) > 0) return false;
  const { data, error } = await supabase
    .from('api_configurations')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('service_name', 'woocommerce')
    .eq('is_active', true)
    .limit(1);
  return !error && (data?.length ?? 0) > 0;
}

export interface JobsFilter {
  storeId?: string | null;
  open?: boolean;
  limit?: number;
  offset?: number;
}

export async function fetchJobs(f: JobsFilter = {}): Promise<QueryResult<CommerceJobRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('product_sync_jobs').select(JOB_COLUMNS).eq('organization_id', org).not('commerce_store_id', 'is', null);
  if (f.storeId) q = q.eq('commerce_store_id', f.storeId);
  q = f.open
    ? q.in('status', ['pending', 'running', 'paused', 'held'])
    : q.in('status', ['completed', 'failed', 'cancelled']);
  const limit = f.limit ?? 50;
  const offset = f.offset ?? 0;
  const { data, error } = await q.order('created_at', { ascending: false }).range(offset, offset + limit - 1);
  return error ? fail(error) : { data: (data ?? []) as unknown as CommerceJobRow[], error: null };
}

export interface EventsFilter {
  storeId?: string | null;
  kinds?: string[] | null;
  entityTypes?: string[] | null;
  onlyErrors?: boolean;
  sinceIso?: string | null;
  limit?: number;
  offset?: number;
}

export async function fetchEvents(f: EventsFilter = {}): Promise<QueryResult<{ rows: CommerceEventRow[]; total: number }>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('commerce_events').select(EVENT_COLUMNS, { count: 'exact' }).eq('organization_id', org);
  if (f.storeId) q = q.eq('store_id', f.storeId);
  if (f.kinds && f.kinds.length > 0) q = q.in('kind', f.kinds);
  if (f.entityTypes && f.entityTypes.length > 0) q = q.in('entity_type', f.entityTypes);
  if (f.onlyErrors) q = q.eq('status', 'error');
  if (f.sinceIso) q = q.gte('created_at', f.sinceIso);
  const limit = f.limit ?? 50;
  const offset = f.offset ?? 0;
  const { data, error, count } = await q.order('created_at', { ascending: false }).range(offset, offset + limit - 1);
  if (error) return fail(error);
  return { data: { rows: (data ?? []) as unknown as CommerceEventRow[], total: count ?? 0 }, error: null };
}

export async function fetchChangeLog(storeId: string, f: { sinceIso?: string | null; jobId?: string | null; limit?: number }): Promise<QueryResult<ChangeLogRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('commerce_change_log').select(CHANGE_COLUMNS).eq('organization_id', org).eq('store_id', storeId);
  if (f.sinceIso) q = q.gte('created_at', f.sinceIso);
  if (f.jobId) q = q.eq('job_id', f.jobId);
  const { data, error } = await q.order('created_at', { ascending: false }).limit(f.limit ?? 100);
  return error ? fail(error) : { data: (data ?? []) as unknown as ChangeLogRow[], error: null };
}

export async function fetchSnapshots(storeId: string, sinceIso?: string | null): Promise<QueryResult<SnapshotRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('commerce_snapshots').select(SNAPSHOT_COLUMNS).eq('organization_id', org).eq('store_id', storeId).neq('status', 'expired');
  if (sinceIso) q = q.gte('created_at', sinceIso);
  const { data, error } = await q.order('created_at', { ascending: false }).limit(100);
  return error ? fail(error) : { data: (data ?? []) as unknown as SnapshotRow[], error: null };
}

export async function fetchStoreJobsSince(storeId: string, sinceIso: string | null, jobId?: string | null): Promise<QueryResult<CommerceJobRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  let q = supabase.from('product_sync_jobs').select(JOB_COLUMNS).eq('organization_id', org).eq('commerce_store_id', storeId);
  if (jobId) q = q.eq('id', jobId);
  else if (sinceIso) q = q.gte('created_at', sinceIso);
  const { data, error } = await q.order('created_at', { ascending: false }).limit(100);
  return error ? fail(error) : { data: (data ?? []) as unknown as CommerceJobRow[], error: null };
}

/**
 * Freni ancora da decidere del negozio (commerce_events kind 'alert', topic brake_*): gli stati chiusi
 * si tolgono gia' qui, il resto lo filtra brakeEvents.ts (details.settled).
 */
export async function fetchBrakeEvents(storeId: string): Promise<QueryResult<CommerceEventRow[]>> {
  const org = await currentOrgId();
  if (!org) return noOrg();
  const { data, error } = await supabase
    .from('commerce_events')
    .select(EVENT_COLUMNS)
    .eq('organization_id', org)
    .eq('store_id', storeId)
    .eq('kind', 'alert')
    .like('topic', 'brake%')
    .not('status', 'in', '(confirmed,done,expired)')
    .order('created_at', { ascending: true })
    .limit(20);
  return error ? fail(error) : { data: (data ?? []) as unknown as CommerceEventRow[], error: null };
}
